import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';

import { requestGetManagers } from '../../services/wisdomServices/managerService.js';
import { updateProjectRequest } from '../../services/wisdomServices/projectService.js';
import { getSessionItem, setSessionItem } from '../../utils/helper/session.js';

import './UpdateProjectModal.css';
import { Can } from '../../security/Can.jsx';
import { PERMISSIONS } from '../../security/permissions.generated.js';

const DEFAULT_TIMEZONE = 'UTC +00:00';
const DEFAULT_INDUSTRY = 'None';
const DEFAULT_QUINNDESK_URL = 'https://quinndesk-url-placeholder.com';

const getTimezoneOffset = (timeZone) => {
  const now = new Date();

  const utcDate = new Date(
    now.toLocaleString('en-US', { timeZone: 'UTC' })
  );
  const timezoneDate = new Date(
    now.toLocaleString('en-US', { timeZone })
  );

  const diffMinutes = (timezoneDate - utcDate) / (1000 * 60);
  const sign = diffMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(diffMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0');
  const minutes = String(absoluteMinutes % 60).padStart(2, '0');

  return `${timeZone} ${sign}${hours}:${minutes}`;
};

const TIMEZONE_OPTIONS = [
  { label: 'UTC', value: DEFAULT_TIMEZONE },
  ...Intl.supportedValuesOf('timeZone').map((timeZone) => ({
    label: timeZone,
    value: getTimezoneOffset(timeZone),
  })),
];

function UpdateProjectModal({ modalData, onClose, saveChanges, open }) {
  const [myData, setMyData] = useState(null);
  const [managers, setManagers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useRef(null);

  const parseTimeString = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr instanceof Date) return timeStr;

    if (typeof timeStr === 'string' && timeStr.includes(':')) {
      const [hours, minutes] = timeStr.split(':');
      const date = new Date();
      date.setHours(Number(hours), Number(minutes), 0, 0);
      return date;
    }

    return null;
  };

  const formatTime = (date) => {
    if (!(date instanceof Date)) return '';
    return date.toTimeString().slice(0, 5);
  };

  useEffect(() => {
    if (!modalData || !open) return;

    const { hourCap, ...rest } = modalData;

    setMyData({
      ...rest,
      hours: hourCap,
      startTime: parseTimeString(modalData.startTime),
      endTime: parseTimeString(modalData.endTime),
      industry: modalData.industry?.trim() || DEFAULT_INDUSTRY,
      timezone:
        modalData.timezone?.trim() === 'UTC'
          ? DEFAULT_TIMEZONE
          : modalData.timezone?.trim() || DEFAULT_TIMEZONE,
      ticketingSystemUrl:
        modalData.ticketingSystemUrl?.trim() || DEFAULT_QUINNDESK_URL,
      knowledgeBaseUrl:
        modalData.knowledgeBaseUrl?.trim() || DEFAULT_QUINNDESK_URL,
      internal: modalData.internal ?? false,
    });
  }, [modalData, open]);

  useEffect(() => {
    const getManagers = async () => {
      try {
        const response = await requestGetManagers();
        const managerList = response.data.map((manager) => ({
          value: manager.id,
          label: `${manager.firstName} ${manager.lastName}`,
        }));
        setManagers(managerList);
      } catch (error) {
        console.error('Error loading managers:', error);
      }
    };

    getManagers();
  }, []);

  const updateField = (field, value) => {
    setMyData((previous) => ({ ...previous, [field]: value }));
  };

  const handleManagerChange = (event) => {
    const selected = managers.find((manager) => manager.value === event.value);

    setMyData((previous) => ({
      ...previous,
      managerId: event.value,
      manager: selected?.label,
    }));
  };

  const handleCoordinateChange = (field, value) => {
    const sanitizedValue = value
      .replace(/[^0-9.-]/g, '')
      .replace(/(?!^)-/g, '');

    updateField(field, sanitizedValue);
  };

  const isFormValid = () =>
    Boolean(
      myData?.name?.trim() &&
        myData?.contactName?.trim() &&
        myData?.managerId &&
        myData?.startTime &&
        myData?.endTime &&
        myData?.industry?.trim() &&
        myData?.timezone?.trim() &&
        myData?.ticketingSystemUrl?.trim() &&
        myData?.knowledgeBaseUrl?.trim() &&
        typeof myData?.internal === 'boolean'
    );

  const showWarning = (text) => {
    toast.current?.show({
      severity: 'warn',
      summary: 'Warning',
      detail: text,
      life: 3000,
    });
  };

  const updateProjectDetails = async () => {
    if (!isFormValid()) {
      showWarning('Please fill all required fields.');
      return;
    }

    try {
      setIsSaving(true);
      const { members, startTime, endTime, ...rest } = myData;

      const payload = {
        ...rest,
        startTime: formatTime(startTime),
        endTime: formatTime(endTime),
        name: myData.name.trim(),
        contactName: myData.contactName.trim(),
        industry: myData.industry.trim(),
        timezone: myData.timezone.trim(),
        ticketingSystemUrl: myData.ticketingSystemUrl.trim(),
        knowledgeBaseUrl: myData.knowledgeBaseUrl.trim(),
        latitude: myData.latitude?.trim(),
        longitude: myData.longitude?.trim(),
      };

      await updateProjectRequest(payload.id, payload);

      const storedProjects = getSessionItem('projects') || [];
      let updatedProjects;

      if (payload.disabled) {
        updatedProjects = storedProjects.filter(
          (project) => project.id !== payload.id
        );
      } else {
        const projectExists = storedProjects.some(
          (project) => project.id === payload.id
        );

        updatedProjects = projectExists
          ? storedProjects.map((project) =>
              project.id === payload.id
                ? { ...project, ...payload }
                : project
            )
          : [...storedProjects, payload];
      }

      setSessionItem('projects', updatedProjects);
      saveChanges();
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!myData) return null;

  const dialogHeader = (
    <div className="update-project-dialog-header">
      <span className="update-project-dialog-eyebrow">Project management</span>
      <span className="update-project-dialog-title">{myData.name}</span>
    </div>
  );

  const dialogFooter = (
    <div className="update-project-dialog-footer">
      <Message severity="info" text="All (*) fields are mandatory" />

      <div className="update-project-dialog-footer-actions">
        <Button
          label="Cancel"
          icon="pi pi-times"
          severity="secondary"
          outlined
          onClick={onClose}
          disabled={isSaving}
        />
        <Can permission={PERMISSIONS.projectsById_put}>
          <Button
            label="Save changes"
            icon="pi pi-check"
            onClick={updateProjectDetails}
            disabled={!isFormValid()}
            loading={isSaving}
          />

        </Can>
      </div>
    </div>
  );

  return (
    <Dialog
      className="update-project-dialog"
      contentClassName="update-project-dialog-content"
      dismissableMask={!isSaving}
      visible={open}
      onHide={onClose}
      header={dialogHeader}
      footer={dialogFooter}
      modal
      draggable={false}
      style={{ width: 'min(1120px, 94vw)' }}
    >
      <Toast ref={toast} position="top-center" />

      <div className="update-project-form">
        <section className="update-project-section">
          <div className="update-project-section-heading">
            <i className="pi pi-briefcase" />
            <div>
              <h3>General information</h3>
              <p>Project identification, ownership and capacity.</p>
            </div>
          </div>

          <div className="update-project-grid update-project-grid--four">
            <div className="update-project-field update-project-field--wide">
              <label htmlFor="update-project-name">Project name *</label>
              <InputText
                id="update-project-name"
                value={myData.name ?? ''}
                onChange={(event) => updateField('name', event.target.value)}
              />
            </div>

            <div className="update-project-field">
              <label htmlFor="update-project-contact-name">Contact name *</label>
              <InputText
                id="update-project-contact-name"
                value={myData.contactName ?? ''}
                onChange={(event) =>
                  updateField('contactName', event.target.value)
                }
              />
            </div>

            <div className="update-project-field">
              <label htmlFor="update-project-manager">Manager *</label>
              <Dropdown
                id="update-project-manager"
                options={managers}
                optionLabel="label"
                optionValue="value"
                value={myData.managerId}
                onChange={handleManagerChange}
                filter
                placeholder="Select a manager"
              />
            </div>

            <div className="update-project-field">
              <label htmlFor="update-project-industry">Industry *</label>
              <InputText
                id="update-project-industry"
                value={myData.industry ?? ''}
                onChange={(event) =>
                  updateField('industry', event.target.value)
                }
              />
            </div>

            <div className="update-project-field">
              <label htmlFor="update-project-hour-cap">Hour cap per agent *</label>
              <InputNumber
                id="update-project-hour-cap"
                value={myData.hours}
                min={0}
                useGrouping={false}
                onValueChange={(event) => updateField('hours', event.value)}
              />
            </div>

            <div className="update-project-field">
              <label htmlFor="update-project-timezone">Timezone *</label>
              <Dropdown
                id="update-project-timezone"
                options={TIMEZONE_OPTIONS}
                optionLabel="label"
                optionValue="value"
                value={myData.timezone}
                onChange={(event) => updateField('timezone', event.value)}
                filter
                filterBy="label"
                placeholder="Select a timezone"
              />
            </div>
          </div>
        </section>

        <section className="update-project-section">
          <div className="update-project-section-heading">
            <i className="pi pi-link" />
            <div>
              <h3>Project resources</h3>
              <p>Links used by the support and operations teams.</p>
            </div>
          </div>

          <div className="update-project-grid update-project-grid--two">
            <div className="update-project-field">
              <label htmlFor="update-project-ticketing-url">
                Ticketing system URL *
              </label>
              <span className="p-input-icon-left">
                <i className="pi pi-ticket" />
                <InputText
                  id="update-project-ticketing-url"
                  type="url"
                  value={myData.ticketingSystemUrl ?? ''}
                  onChange={(event) =>
                    updateField('ticketingSystemUrl', event.target.value)
                  }
                />
              </span>
            </div>

            <div className="update-project-field">
              <label htmlFor="update-project-knowledge-base-url">
                Knowledge base URL *
              </label>
              <span className="p-input-icon-left">
                <i className="pi pi-book" />
                <InputText
                  id="update-project-knowledge-base-url"
                  type="url"
                  value={myData.knowledgeBaseUrl ?? ''}
                  onChange={(event) =>
                    updateField('knowledgeBaseUrl', event.target.value)
                  }
                />
              </span>
            </div>
          </div>
        </section>

        <section className="update-project-section">
          <div className="update-project-section-heading">
            <i className="pi pi-chart-line" />
            <div>
              <h3>Activity thresholds</h3>
              <p>Optional monitoring limits for desktop activity.</p>
            </div>
          </div>

          <div className="update-project-grid update-project-grid--four">
            {[
              ['maxKeystrokes', 'Max keystrokes'],
              ['maxMouseClicks', 'Max mouse clicks'],
              ['maxRepeatingKeystrokes', 'Repeating keystrokes'],
              ['maxRepeatingMouseClicks', 'Repeating mouse clicks'],
            ].map(([field, label]) => (
              <div className="update-project-field" key={field}>
                <label htmlFor={`update-project-${field}`}>{label}</label>
                <InputNumber
                  id={`update-project-${field}`}
                  value={myData[field]}
                  min={0}
                  useGrouping={false}
                  onValueChange={(event) => updateField(field, event.value)}
                />
              </div>
            ))}
          </div>
        </section>

        <div className="update-project-section-row">
          <section className="update-project-section">
            <div className="update-project-section-heading">
              <i className="pi pi-map-marker" />
              <div>
                <h3>Location</h3>
                <p>Geofence coordinates and radius.</p>
              </div>
            </div>

            <div className="update-project-grid update-project-grid--three">
              <div className="update-project-field">
                <label htmlFor="update-project-latitude">Latitude</label>
                <InputText
                  id="update-project-latitude"
                  placeholder="e.g. -23.5505"
                  value={myData.latitude ?? ''}
                  onChange={(event) =>
                    handleCoordinateChange('latitude', event.target.value)
                  }
                />
              </div>

              <div className="update-project-field">
                <label htmlFor="update-project-longitude">Longitude</label>
                <InputText
                  id="update-project-longitude"
                  placeholder="e.g. -46.6333"
                  value={myData.longitude ?? ''}
                  onChange={(event) =>
                    handleCoordinateChange('longitude', event.target.value)
                  }
                />
              </div>

              <div className="update-project-field">
                <label htmlFor="update-project-radius">Radius</label>
                <InputNumber
                  id="update-project-radius"
                  value={myData.radius ?? 0}
                  min={0}
                  useGrouping={false}
                  onValueChange={(event) => updateField('radius', event.value)}
                />
              </div>
            </div>
          </section>

          <section className="update-project-section">
            <div className="update-project-section-heading">
              <i className="pi pi-clock" />
              <div>
                <h3>Coverage</h3>
                <p>Daily operating window in the selected timezone.</p>
              </div>
            </div>

            <div className="update-project-grid update-project-grid--two">
              <div className="update-project-field">
                <label htmlFor="update-project-coverage-start">Start *</label>
                <Calendar
                  id="update-project-coverage-start"
                  value={myData.startTime}
                  onChange={(event) => updateField('startTime', event.value)}
                  timeOnly
                  hourFormat="24"
                  showIcon
                />
              </div>

              <div className="update-project-field">
                <label htmlFor="update-project-coverage-end">End *</label>
                <Calendar
                  id="update-project-coverage-end"
                  value={myData.endTime}
                  onChange={(event) => updateField('endTime', event.value)}
                  timeOnly
                  hourFormat="24"
                  showIcon
                />
              </div>
            </div>
          </section>
        </div>

        <section className="update-project-section">
          <div className="update-project-section-heading">
            <i className="pi pi-cog" />
            <div>
              <h3>Project settings</h3>
              <p>Availability and access options.</p>
            </div>
          </div>

          <div className="update-project-switch-grid">
            {[
              ['disabled', 'Disabled', 'Hide this project from active lists.'],
              ['isSharedService', 'Shared service', 'Allow shared service usage.'],
              ['mobileTracker', 'Mobile app', 'Enable mobile time tracking.'],
              ['internal', 'Internal *', 'Mark this as an internal project.'],
            ].map(([field, label, description]) => (
              <div className="update-project-switch-card" key={field}>
                <div>
                  <label htmlFor={`update-project-${field}`}>{label}</label>
                  <small>{description}</small>
                </div>
                <InputSwitch
                  id={`update-project-${field}`}
                  checked={Boolean(myData[field])}
                  onChange={(event) => updateField(field, event.value)}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </Dialog>
  );
}

export default UpdateProjectModal;